import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchTerms, saveTerms } from '../../../services/setupService';

// What would actually be persisted: trimmed, non-empty lines in order.
const normalize = (lines) =>
  (Array.isArray(lines) ? lines : [])
    .map((line) => (line == null ? '' : String(line).trim()))
    .filter((line) => line !== '');

export default function TermsAndConditionsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lines, setLines] = useState([]);
  const [savedLines, setSavedLines] = useState([]);
  const [justSaved, setJustSaved] = useState(false);

  const dirty = useMemo(
    () => JSON.stringify(normalize(lines)) !== JSON.stringify(savedLines),
    [lines, savedLines]
  );

  const load = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const data = await fetchTerms();
      setLines(data.length ? data : ['']);
      setSavedLines(data);
      setJustSaved(false);
    } catch (e) {
      console.error('Failed to load terms and conditions:', e);
      setError(e?.message || 'Failed to load terms and conditions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeLine = (index) => (e) => {
    const next = e.target.value;
    setJustSaved(false);
    setLines((prev) => prev.map((line, i) => (i === index ? next : line)));
  };

  const addLine = () => {
    setJustSaved(false);
    setLines((prev) => [...prev, '']);
  };

  const removeLine = (index) => {
    setJustSaved(false);
    setLines((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [''];
    });
  };

  const moveLine = (index, direction) => {
    setJustSaved(false);
    setLines((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const onSave = async () => {
    try {
      setError('');
      setSaving(true);
      const saved = await saveTerms(lines);
      setLines(saved.length ? saved : ['']);
      setSavedLines(saved);
      setJustSaved(true);
    } catch (e) {
      console.error('Failed to save terms and conditions:', e);
      setError(e?.message || 'Failed to save terms and conditions.');
      alert('Failed to save terms: ' + (e?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 mb-1">
      <div className="text-md font-semibold text-gray-900">Terms and Conditions</div>
        <div className="flex items-center gap-3">
          {justSaved && !dirty && (
            <span className="text-xs font-medium text-emerald-700">✓ Saved</span>
          )}
          {dirty && !saving && (
            <span className="text-xs font-medium text-amber-700">Unsaved changes</span>
          )}
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={saving || loading || !dirty}
            title={!dirty ? 'No changes to save' : ''}
          >
            {saving ? 'Saving…' : 'Save terms'}
          </button>
        </div>
      </div>

      {error && (
        <div className="my-3 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="mt-3 text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {lines.map((line, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="mt-2 w-6 shrink-0 text-xs font-medium text-gray-400 text-right">
                {index + 1}.
              </span>
              <textarea
                value={line}
                onChange={onChangeLine(index)}
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-y"
                placeholder="Enter a terms and conditions line"
              />
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => moveLine(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                  className="px-2 py-1 bg-white text-gray-600 border border-gray-200 rounded-md text-xs cursor-pointer hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveLine(index, 1)}
                  disabled={index === lines.length - 1}
                  title="Move down"
                  className="px-2 py-1 bg-white text-gray-600 border border-gray-200 rounded-md text-xs cursor-pointer hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ↓
                </button>
              </div>
              <button
                type="button"
                onClick={() => removeLine(index)}
                title="Delete line"
                className="mt-0.5 px-2 py-2 bg-white text-red-700 border border-red-200 rounded-md text-xs font-medium cursor-pointer hover:bg-red-50"
              >
                ✕
              </button>
            </div>
          ))}

          <div>
            <button
              type="button"
              onClick={addLine}
              className="mt-1 px-3 py-2 bg-white text-gray-700 border border-gray-200 rounded-md cursor-pointer text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              + Add line
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
